import {useCallback, useMemo} from 'react';
import {skipToken} from '@apollo/client';

import {AppPermissionId, AppUserPermission, PeriodType, EnnablRoleHierarchyType} from 'modules/decisionsNext/api/types';

import {useCountPoliciesSuspenseQuery} from 'api/aggregatePolicy/countPolicies.generated';
import {FeatureFlags, FeatureFlagsGroup} from 'core/constants/featureFlags';
import {
  useAppPermissions,
  useFeatureFlag,
  useFeatureFlagGroup,
  useIsExportHidden,
  useIsMgaDashboard,
  useIsProducer,
  useRestrictedAccessPermissions,
  useUserInfo,
} from 'core/hooks/authHooks';
import {useProducerViewWithCarrierAppetiteFlag} from 'utils/hooks/useProducerViewWithCarrierAppetiteFlag';
import {useProjectName} from 'utils/hooks/useProjectName';
import {isDesktop} from 'utils/deviceInfo';

import {useUserAgreement} from 'core/components/UserAgreementGate/hooks/useUserAgreement';

const globalFilterPermissions = [
  AppUserPermission.DecisionsGeneral,
  AppUserPermission.DecisionsAccountExecutives,
  AppUserPermission.DecisionsAccountManagers,
  AppUserPermission.DecisionsRenewals,
  AppUserPermission.Mga,
];

const DECISIONS_PERMISSION = 'DECISIONS';
const GROWTH_PERMISSIONS = [AppUserPermission.GrowthGeneral, AppUserPermission.GrowthMyGoals];
const WORKFLOWS_PERMISSION = 'WORKFLOWS';

const emptyAccess = {
  withDataManagement: false,
  withMga: false,
  withEnnablAI: false,
  withCommand: false,
  withDecisions: false,
  withMarkets: false,
  withProducts: false,
  withAccounts: false,
  withIndustries: false,
  withExportReports: false,
  withActivities: false,
  withRetention: false,
  withO360Score: false,
  withMonolineRatios: false,
  withProducers: false,
  withAccountManagers: false,
  withAccountExecutives: false,
  withCarrierAppetite: false,
  withReportsStudio: false,
  withRenewals: false,
  withKpis: false,
  withWhatsNew: false,
  withCustomHomePage: false,
  withGlobalFilter: false,
  withGrowth: false,
  withMyGoals: false,
  withInsights: false,
  withWorkflows: false,
  withLossRuns: false,
  withDocumentsLibrary: false,
  withDocumentsCompare: false,
  withProposalGeneration: false,
  withSubmissionGeneration: false,
  withAcordPrefill: false,
  withSoiGeneration: false,
  withAmsDocuments: false,
  withAmsAudit: false,
  withUserManagement: false,
  withUserEnvironment: false,
  withUserManagementOnly: false,
  withDataManagementOnly: false,
  withWorkflowsDemoMode: false,
};

export const useEntityAccess = (): Record<keyof typeof emptyAccess, boolean> => {
  const withNewPermissions = useFeatureFlag(FeatureFlags.UserManagement);
  const {role, ennablEmployeeId} = useUserInfo();

  const {hasAcceptedUserAgreement} = useUserAgreement();
  const withWhatsNew = useFeatureFlag(FeatureFlags.WhatsNew);
  const withDataManagement = useFeatureFlag(FeatureFlags.SourceDataManagement);
  const withSubAdminPermissions = useFeatureFlag(FeatureFlags.SubAdminPermissions);
  const isSubmission = useFeatureFlag(FeatureFlags.SubmissionGeneration);
  const withWorkflowsDemoMode = useFeatureFlag(FeatureFlags.WorkflowsDemoMode);
  const {isProducerWithCarrierAppetite} = useProducerViewWithCarrierAppetiteFlag();
  const isProducer = useIsProducer();
  const {isMga} = useProjectName();

  const isDataManagementUser =
    withDataManagement && role?.appPermissions.includes(AppUserPermission.SourceDataManagement);

  const countPoliciesQuery = useCountPoliciesSuspenseQuery(
    isDataManagementUser
      ? {
          variables: {
            filter: {
              period: {
                type: PeriodType.AllTime,
              },
            },
          },
        }
      : skipToken
  );

  // TODO: Remove when all tenants are migrated to new permissions
  const oldEntityAccess = useOldEntityAccess();

  const entityAccess = useMemo(() => {
    if (!role?.appPermissions) {
      return emptyAccess;
    }

    const permissionsSet = new Set(role.appPermissions);
    const hasPermission = (p: AppUserPermission) => permissionsSet.has(p);

    if (!hasAcceptedUserAgreement) {
      return emptyAccess;
    }

    if (isDataManagementUser && !countPoliciesQuery.data?.aggregatePolicy.metric) {
      return {...emptyAccess, withDataManagement: true, withDataManagementOnly: true};
    }

    const withGrowth = role.appPermissions.some(permission => GROWTH_PERMISSIONS.includes(permission));

    const withWorkflows = role.appPermissions.some(permission => permission.includes(WORKFLOWS_PERMISSION));

    const workflowsAccess = {
      withWorkflows: withWorkflows || withWorkflowsDemoMode,
      withLossRuns: !withWorkflowsDemoMode && !isSubmission && hasPermission(AppUserPermission.WorkflowsLossRuns),
      withDocumentsLibrary:
        !withWorkflowsDemoMode && !isSubmission && hasPermission(AppUserPermission.WorkflowsDocLibrary),
      withDocumentsCompare:
        !withWorkflowsDemoMode && !isSubmission && hasPermission(AppUserPermission.WorkflowsDocCompare),
      withProposalGeneration:
        !withWorkflowsDemoMode && !isSubmission && hasPermission(AppUserPermission.WorkflowsProposalGeneration),
      withSubmissionGeneration:
        !withWorkflowsDemoMode && isSubmission && hasPermission(AppUserPermission.WorkflowsSubmissionGeneration),
      withSoiGeneration:
        !withWorkflowsDemoMode && !isSubmission && hasPermission(AppUserPermission.WorkflowsSoiGeneration),
      withAcordPrefill:
        !withWorkflowsDemoMode && !isSubmission && hasPermission(AppUserPermission.WorkflowsAcordPrefill),
      withAmsDocuments:
        !withWorkflowsDemoMode && !isSubmission && hasPermission(AppUserPermission.WorkflowsLegacyAmsDocuments),
      withAmsAudit:
        !withWorkflowsDemoMode && !isSubmission && hasPermission(AppUserPermission.WorkflowsWriteBackPushToAms),
      withWorkflowsDemoMode,
    };

    if (isProducerWithCarrierAppetite) {
      return {
        ...emptyAccess,
        withCarrierAppetite: true,
        withInsights: false,
        withUserEnvironment: false,
        withMga: hasPermission(AppUserPermission.Mga),
        withEnnablAI: hasPermission(AppUserPermission.EnnablAi),
        withCommand: hasPermission(AppUserPermission.EnnablData) && isDesktop,
        withGrowth,
        withMyGoals: !!ennablEmployeeId && hasPermission(AppUserPermission.GrowthMyGoals),

        ...workflowsAccess,
      };
    }

    const withUserManagement = hasPermission(AppUserPermission.UserManagement);

    // Need to give decisions access to workflows/ennablAI users to show decisions home page
    const withDecisions =
      role.appPermissions.some(
        permission => permission.includes(DECISIONS_PERMISSION) && permission !== AppUserPermission.DecisionsExport
      ) ||
      hasPermission(AppUserPermission.EnnablAi) ||
      withWorkflows;

    return {
      ...emptyAccess,
      withDecisions,
      withDataManagement: withDataManagement && hasPermission(AppUserPermission.SourceDataManagement),
      withMga: hasPermission(AppUserPermission.Mga),
      withEnnablAI: hasPermission(AppUserPermission.EnnablAi),
      withCommand: hasPermission(AppUserPermission.EnnablData) && isDesktop,
      withMarkets: hasPermission(AppUserPermission.DecisionsGeneral),
      withProducts: hasPermission(AppUserPermission.DecisionsGeneral),
      withAccounts: hasPermission(AppUserPermission.DecisionsGeneral),
      withIndustries: hasPermission(AppUserPermission.DecisionsGeneral),
      withExportReports: hasPermission(AppUserPermission.DecisionsExport),
      withActivities: hasPermission(AppUserPermission.DecisionsActivities),
      withRetention: hasPermission(AppUserPermission.DecisionsRetention),
      withO360Score: hasPermission(AppUserPermission.DecisionsO360),
      withMonolineRatios: hasPermission(AppUserPermission.DecisionsMonolineRatios),
      withProducers: !isProducer && (hasPermission(AppUserPermission.DecisionsGeneral) || isMga),
      withAccountManagers: hasPermission(AppUserPermission.DecisionsAccountManagers),
      withAccountExecutives: hasPermission(AppUserPermission.DecisionsAccountExecutives),
      withCarrierAppetite: hasPermission(AppUserPermission.DecisionsCarrierAppetite),
      withReportsStudio: hasPermission(AppUserPermission.DecisionsReportStudio),
      withRenewals: hasPermission(AppUserPermission.DecisionsRenewals),
      withKpis: hasPermission(AppUserPermission.DecisionsKpis),
      withWhatsNew: withWhatsNew && !isProducer,
      withCustomHomePage: hasPermission(AppUserPermission.DecisionsHomePageDashboards) && !isProducer,
      withGlobalFilter: globalFilterPermissions.some(permission => hasPermission(permission)),
      withGrowth,
      withMyGoals: !!ennablEmployeeId && hasPermission(AppUserPermission.GrowthMyGoals),
      withInsights: role.appPermissions.some(permission => permission.includes(DECISIONS_PERMISSION)) || withWorkflows,

      ...workflowsAccess,

      withUserManagement,
      withUserEnvironment:
        withUserManagement && withSubAdminPermissions && role.roleType === EnnablRoleHierarchyType.Root,
      withUserManagementOnly: withUserManagement && !withDecisions,
    };
  }, [
    role,
    hasAcceptedUserAgreement,
    isDataManagementUser,
    countPoliciesQuery.data?.aggregatePolicy.metric,
    withDataManagement,
    withWorkflowsDemoMode,
    isSubmission,
    isProducerWithCarrierAppetite,
    isProducer,
    isMga,
    withWhatsNew,
    ennablEmployeeId,
    withSubAdminPermissions,
  ]);

  return withNewPermissions ? entityAccess : oldEntityAccess;
};

const useOldEntityAccess = () => {
  const {hasPermission} = useAppPermissions();
  const {ennablEmployeeId} = useUserInfo();
  const isProducer = useIsProducer();
  const {restrictedAccess, onlyReportStudioPermission, onlyDocumentsPermission} = useRestrictedAccessPermissions();
  const {isProducerWithCarrierAppetite} = useProducerViewWithCarrierAppetiteFlag();
  const withExportReports = !useIsExportHidden();
  const withEnnablAI = useFeatureFlag(FeatureFlags.EnnablAi);
  const withAccountExecutives = useFeatureFlag(FeatureFlags.AccountExecutives);
  const withMonolineRatios = useFeatureFlag(FeatureFlags.MonolineRatios);
  const withActivities = useFeatureFlag(FeatureFlags.Activities);
  const withRetention = useFeatureFlag(FeatureFlags.Retention);
  const withMga = useIsMgaDashboard();
  const withO360Score = useFeatureFlag(FeatureFlags.O360Score);
  const withWhatsNew = useFeatureFlag(FeatureFlags.WhatsNew);
  const withDecisions = !withMga && hasPermission(AppPermissionId.ViewDecisions);
  const withCommand = hasPermission(AppPermissionId.EditCommand) && !restrictedAccess && isDesktop;
  const withCustomHomePage = useFeatureFlag(FeatureFlags.CustomHomePage);

  const withWorkflows = useFeatureFlagGroup(FeatureFlagsGroup.Workflows);
  const withLossRuns = useFeatureFlag(FeatureFlags.LossRuns);
  const withDocsLibrary = useFeatureFlag(FeatureFlags.DocsLibrary);
  const withProposalGeneration = useFeatureFlag(FeatureFlags.ProposalGeneration);
  const withSoiGeneration = useFeatureFlag(FeatureFlags.SoiGeneration);
  const withDocumentsCompare = useFeatureFlag(FeatureFlags.DocsCompare);
  const withAmsDocuments = useFeatureFlag(FeatureFlags.AmsDocuments);
  const withAmsAudit = useFeatureFlag(FeatureFlags.AmsAudit);
  const withSubmissionGeneration = useFeatureFlag(FeatureFlags.SubmissionGeneration);
  const withAcordPrefill = useFeatureFlag(FeatureFlags.AcordPrefill);
  const withWorkflowsDemoMode = useFeatureFlag(FeatureFlags.WorkflowsDemoMode);

  const withRegularWorkflow = !withWorkflowsDemoMode && !withMga && !onlyReportStudioPermission;

  return useMemo(
    () => ({
      ...emptyAccess,
      withDecisions: withDecisions && !isProducerWithCarrierAppetite,
      withMga: withMga && !restrictedAccess && !isProducerWithCarrierAppetite,
      withGrowth: !withMga && !restrictedAccess,
      withInsights: !withMga && !restrictedAccess,
      withMyGoals: !withMga && !restrictedAccess && !!ennablEmployeeId,
      withCommand: withCommand && !isProducerWithCarrierAppetite,

      withEnnablAI: withEnnablAI && !isProducerWithCarrierAppetite,
      withCustomHomePage: withCustomHomePage && !isProducer,
      withExportReports,
      withCarrierAppetite: true,
      withMonolineRatios: withMonolineRatios && !isProducerWithCarrierAppetite && !withMga,
      withActivities: withActivities && !isProducerWithCarrierAppetite && !withMga,
      withRetention: withRetention && !isProducerWithCarrierAppetite && !withMga,
      withReportsStudio: withExportReports && !isProducerWithCarrierAppetite && !withMga,
      withO360Score: withO360Score && !isProducerWithCarrierAppetite && !withMga,
      withWhatsNew: withWhatsNew && withDecisions && !isProducerWithCarrierAppetite && !withMga,
      withAccountExecutives: withAccountExecutives && !isProducer && !withMga,
      withProducers: !isProducer,
      withAccountManagers: !isProducer && !withMga,
      withMarkets: !isProducerWithCarrierAppetite,
      withProducts: !isProducerWithCarrierAppetite && !withMga,
      withAccounts: !isProducerWithCarrierAppetite && !withMga && !onlyDocumentsPermission,
      withIndustries: !isProducerWithCarrierAppetite && !withMga,
      withRenewals: !isProducerWithCarrierAppetite && !withMga,
      withKpis: !isProducerWithCarrierAppetite && !withMga,
      withGlobalFilter: !isProducerWithCarrierAppetite,

      withWorkflows: (withWorkflows && !withMga && !onlyReportStudioPermission) || withWorkflowsDemoMode,
      withLossRuns: withLossRuns && !withSubmissionGeneration && withRegularWorkflow,
      withDocumentsLibrary: withDocsLibrary && !withSubmissionGeneration && withRegularWorkflow,
      withDocumentsCompare: withDocumentsCompare && !withSubmissionGeneration && withRegularWorkflow,
      withProposalGeneration: withProposalGeneration && !withSubmissionGeneration && withRegularWorkflow,
      withSoiGeneration: withSoiGeneration && !withSubmissionGeneration && withRegularWorkflow,
      withSubmissionGeneration: withSubmissionGeneration && withRegularWorkflow,
      withAcordPrefill: withAcordPrefill && !withSubmissionGeneration && withRegularWorkflow,
      withAmsDocuments: withAmsDocuments && !withSubmissionGeneration && withRegularWorkflow,
      withAmsAudit: withAmsAudit && !withSubmissionGeneration && withRegularWorkflow,
      withWorkflowsDemoMode,
    }),
    [
      withDecisions,
      isProducerWithCarrierAppetite,
      withMga,
      restrictedAccess,
      withCustomHomePage,
      withLossRuns,
      onlyReportStudioPermission,
      withWorkflows,
      withCommand,
      withEnnablAI,
      withExportReports,
      withMonolineRatios,
      withActivities,
      withRetention,
      withAmsDocuments,
      withO360Score,
      withWhatsNew,
      withDocumentsCompare,
      withProposalGeneration,
      withAccountExecutives,
      withSubmissionGeneration,
      isProducer,
      onlyDocumentsPermission,
      withAmsAudit,
      ennablEmployeeId,
      withDocsLibrary,
      withAcordPrefill,
      withWorkflowsDemoMode,
      withRegularWorkflow,
      withSoiGeneration,
    ]
  );
};

export const useCheckEntityAccess = () => {
  const accessMap = useEntityAccess();

  return useCallback(
    (permission: keyof ReturnType<typeof useEntityAccess>) => {
      return !!accessMap[permission];
    },
    [accessMap]
  );
};
